import type { OptionsDocument, QuoteDocument, QuoteStatus } from 'paintscout'
import React from 'react'
import type { QuoteAlert } from '../'
import { formatCurrency } from '../../../util/format-currency'
import { formatProcessingPaymentBullet, hasProcessingPayment } from '../../payments'
import { getPayments } from '../../payments/get-payments'
import { getFeature } from '../../../options/get-features'
import { getObjectLabels } from '../../../util/get-object-labels'
import { getPayrixCredentials } from '../../../../util/getPayrixCredentials'
import { showHiddenWarning } from './show-hidden-warning'
import { getIndefiniteArticle } from '../../../../util/getIndefiniteArticle'

export interface GetQuoteAlertsProps {
  quote: QuoteDocument
  options: OptionsDocument
  isCustomerView?: boolean
}

/**
 * Customer-facing copy for a paymentError-locked quote — shown as the customer-view banner here
 * and as the snackbar wherever a payment attempt is blocked (PaymentRequest, the Payrix forms).
 */
export const PAYMENT_PENDING_MESSAGE = 'Hang tight! Your payment is pending. Please check back later.'
export function getQuoteAlerts({ quote, options, isCustomerView }: GetQuoteAlertsProps): QuoteAlert[] {
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const hasSmartTemplate = getFeature({ options, path: 'quotes.smartTemplate' })
  const alerts = [] as QuoteAlert[]

  if (isCustomerView) {
    if (quote.paymentError) {
      alerts.push({
        severity: 'warning',
        content: PAYMENT_PENDING_MESSAGE
      })
    }

    // Never show `status.note` here: it is the contractor's internal "Status Change Reason" (ENG-2011).
    const status = quote.status?.value
    if (status === 'accepted' || status === 'declined') {
      alerts.push({
        severity: getStatusNoteSeverity(status) as QuoteAlert['severity'],
        content:
          status === 'accepted'
            ? `This ${objectLabels.quote.value.toLowerCase()} has been accepted.`
            : `This ${objectLabels.quote.value.toLowerCase()} has been declined.`
      })
    }
  } else {
    if (quote.paymentError) {
      alerts.push({
        severity: 'error',
        content: React.createElement(
          React.Fragment,
          {},
          `A payment error occurred, and customer payments have been temporarily disabled on this ${objectLabels.quote.value.toLowerCase()}. The payment may have still gone through — please `,
          React.createElement(
            'a',
            { href: 'mailto:support@paintscout.com', style: { color: 'inherit' } },
            'contact our support team'
          ),
          ' to confirm and resolve it.'
        )
      })
    }

    if (hasProcessingPayment({ quote })) {
      const processingPayments = getPayments({ quote }).filter((p) => p.status === 'processing')
      const quoteLabel = objectLabels.quote.value.toLowerCase()
      const { portalUrl } = getPayrixCredentials(options)
      const portalLink = `${portalUrl}/payments`
      const isPlural = processingPayments.length > 1
      const intro = isPlural
        ? `Customer payments are currently being processed for this ${quoteLabel}. Please `
        : `A customer payment is currently being processed for this ${quoteLabel}. Please `
      const bullets = processingPayments.map((p) => formatProcessingPaymentBullet({ options, payment: p }))
      alerts.push({
        severity: 'info',
        content: React.createElement(
          React.Fragment,
          {},
          intro,
          React.createElement(
            'a',
            { href: portalLink, target: '_blank', rel: 'noopener noreferrer', style: { color: 'inherit' } },
            'check the portal'
          ),
          ' for updates.',
          bullets.length > 0 &&
            React.createElement(
              'ul',
              { style: { margin: '4px 0 0' } },
              ...bullets.map((b) => React.createElement('li', { key: b }, b))
            )
        ) as any as string
      })
    }

    if (quote.source?.provider === 'pep-import') {
      alerts.push({
        severity: 'info',
        content: `This ${objectLabels.quote.value.toLowerCase()} was imported from PEP and should be used for reference only. To send the same ${objectLabels.quote.value.toLowerCase()}, we recommend copying it and confirming all values.`
      })
    }

    if (quote.source?.providerData?.pepTotal) {
      alerts.push({
        severity: 'warning',
        content: `Unable to reconcile total from PEP (${formatCurrency({ options, value: quote.source?.providerData?.pepTotal })}).`
      })
    }

    if (quote.sendError) {
      alerts.push({
        severity: 'error',
        content: quote.errorMessage
          ? quote.errorMessage
          : `An error occurred sending this ${objectLabels.quote.value.toLowerCase()}.`,
        tooltip: 'Clear this error by sending it again.'
      })
    } else if (quote.archived) {
      alerts.push({
        severity: 'warning',
        content: `This ${objectLabels.quote.value.toLowerCase()} is archived.`
      })
    }

    if (quote.clientVisible === false) {
      alerts.push({
        severity: 'warning',
        content: `This ${objectLabels.quote.value.toLowerCase()} is currently hidden from customers. Activate it through the sidebar "Advanced" tab.`
      })
    }
    if (quote.crewVisible === false) {
      alerts.push({
        severity: 'warning',
        content: `This ${objectLabels.workOrder.value.toLowerCase()} is hidden from crews. Activate it through the sidebar "Advanced" tab.`
      })
    }

    if (showHiddenWarning(quote, options)) {
      alerts.push({
        severity: 'warning',
        content: `This ${objectLabels.quote.value.toLowerCase()} has hidden items that may affect the listed prices correctly adding to the total price. To access and modify these hidden areas, please click the ${
          objectLabels.workOrder.value
        } tab.`
      })
    }

    // no quote alerts for status in SB
    if (quote.status?.note?.value) {
      alerts.push({
        severity: getStatusNoteSeverity(quote?.status?.value) as QuoteAlert['severity'],
        content: quote.status.note.value
      })
    }

    if (quote.type === 'quoteTemplate' && hasSmartTemplate) {
      const legacyTemplates = options.options?.quoteTemplates?.order.filter(
        (templateId) => !options.options.quoteTemplates?.values[templateId]?.smartTemplate
      )

      if (legacyTemplates.length > 0) {
        if (quote?.smartTemplate) {
          alerts.push({
            severity: 'info',
            content: `This is ${getIndefiniteArticle(objectLabels.quote.value)} ${objectLabels.quote.value.toLowerCase()} template: the production rates, products, hourly rate, and terms are not customizable and will automatically use the latest version from your settings and the selected ${objectLabels.quote.value.toLowerCase()} type.`
          })
        } else {
          alerts.push({
            severity: 'info',
            content: React.createElement(
              React.Fragment,
              {},
              `Everything on this template is fully customizable but will `,
              React.createElement('strong', {}, 'not'),
              ` automatically update with the latest production rates, products, or hourly rate from your settings. To work with the latest version of information from your settings and selected ${objectLabels.quote.value.toLowerCase()} type, we recommend creating a new ${objectLabels.quote.value.toLowerCase()} template, which will utilize our `,
              React.createElement(
                'a',
                {
                  href: 'https://help.paintscout.com/support/customization/templates-and-presets/smart-templates',
                  target: '_blank',
                  rel: 'noopener noreferrer'
                },
                'Smart Template'
              ),
              ` functionality.`
            ) as any as string
          })
        }
      } else {
        alerts.push({
          severity: 'info',
          content: `This is ${getIndefiniteArticle(objectLabels.quote.value)} ${objectLabels.quote.value.toLowerCase()} template: the production rates, products, hourly rate, and terms are not customizable and will automatically use the latest version from your settings and the selected ${objectLabels.quote.value.toLowerCase()} type.`
        })
      }
    }
  }

  function getStatusNoteSeverity(status: QuoteStatus) {
    switch (status) {
      case 'accepted':
        return 'success'
      case 'declined':
        return 'error'
      case 'onHold':
        return 'warning'
      default:
        return 'default'
    }
  }

  return alerts
}
